import { Bell, Bot, CircleUserRound, Keyboard, LifeBuoy, LogOut, Megaphone, Mic, Monitor, PanelsTopLeft, Palette, Pencil } from 'lucide-react'
import { useAppStore, type SettingsSection } from '@/store/app'
import { useIsMobile } from '@/lib/mobile'
import { avatarUrl } from '@/lib/avatar'
import { useAuthStore } from '@/store/auth'
import { Avatar } from '../Avatar'
import { PlusMark } from '../PlusMark'
import { StyledName } from '../StyledName'
import { SettingsFrame, type FrameGroup } from './SettingsFrame'
import { AccountSection } from './AccountSection'
import { ProfileSection } from './ProfileSection'
import { AppearanceSection } from './AppearanceSection'
import { VoiceSection } from './VoiceSection'
import { KeybindsSection } from './KeybindsSection'
import { NotificationsSection } from './NotificationsSection'
import { ReleasesSection } from './ReleasesSection'
import { ApplicationsSection } from './ApplicationsSection'
import { DesktopSection } from './DesktopSection'
import { OverlaySection } from './OverlaySection'
import { desktop } from '@/lib/desktop'
import { PremiumSection } from './PremiumSection'
import { HelpSection } from './HelpSection'
import styles from './SettingsLayer.module.css'

const ICON = 20

/**
 * Настройки пользователя — окно поверх приложения (SettingsFrame). Сверху карточка профиля:
 * она и есть раздел «Профиль». Магазина и достижений здесь нет — они на главной, рядом
 * с «Друзьями»: раньше одни и те же разделы жили в двух местах.
 */
export function SettingsLayer() {
  const section = useAppStore((s) => s.settings)
  return section ? <UserSettings section={section} /> : null
}

function UserSettings({ section }: { section: SettingsSection }) {
  const openSettings = useAppStore((s) => s.openSettings)
  const closeSettings = useAppStore((s) => s.closeSettings)
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const staff = ((user?.flags ?? 0) & 1) !== 0
  const mobile = useIsMobile()
  // «Активные сеансы» — часть учётной записи; старый адрес раздела ведёт туда же
  const active = section === 'devices' ? 'account' : section

  const groups: FrameGroup[] = [
    {
      key: 'me',
      items: [
        { id: 'account', label: 'Учётная запись', icon: <CircleUserRound size={ICON} /> },
        { id: 'premium', label: 'Плюс и бусты', icon: <PlusMark size={ICON} label={null} /> },
      ],
    },
    {
      key: 'app',
      title: 'Настройки приложения',
      items: [
        { id: 'voice', label: 'Голос и видео', icon: <Mic size={ICON} /> },
        { id: 'notifications', label: 'Уведомления', icon: <Bell size={ICON} /> },
        // на телефоне клавиатуры нет — раздел с сочетаниями там только мешал бы
        ...(mobile ? [] : [{ id: 'keybinds', label: 'Горячие клавиши', icon: <Keyboard size={ICON} /> }]),
        { id: 'appearance', label: 'Внешний вид', icon: <Palette size={ICON} /> },
        { id: 'desktop', label: 'Приложение', icon: <Monitor size={ICON} /> },
        ...(desktop?.overlay ? [{ id: 'overlay', label: 'Игровой оверлей', icon: <PanelsTopLeft size={ICON} /> }] : []),
      ],
    },
    { key: 'dev', title: 'Разработчикам', items: [{ id: 'applications', label: 'Боты', icon: <Bot size={ICON} /> }] },
    ...(staff
      ? [{
          key: 'staff',
          title: 'Персоналу',
          items: [
            { id: 'releases', label: 'Что нового', icon: <Megaphone size={ICON} /> },
          ],
        }]
      : []),
    { key: 'help', items: [{ id: 'help', label: 'Поддержка и новости', icon: <LifeBuoy size={ICON} /> }] },
    {
      key: 'out',
      items: [{
        id: 'logout',
        label: 'Выйти',
        icon: <LogOut size={ICON} />,
        danger: true,
        onClick: () => {
          closeSettings()
          void signOut()
        },
      }],
    },
  ]

  const name = user ? user.global_name ?? user.username : ''
  const head = (
    <>
      <Avatar initial={name.slice(0, 1).toUpperCase()} color="#5865F2" size={40} src={user ? avatarUrl(user.id, user.avatar) : undefined} decoration={user?.avatar_decoration_data} />
      <span className={styles.navHeadText}>
        <StyledName name={name} style={user?.display_name_styles} className={styles.navHeadName} />
        <span className={styles.navHeadSub}>
          <span>Редактировать профиль</span>
          <Pencil size={12} aria-hidden="true" />
        </span>
      </span>
    </>
  )

  return (
    <SettingsFrame
      label="Настройки пользователя"
      listTitle="Настройки"
      closeLabel="Закрыть настройки"
      head={{ id: 'profile', node: head }}
      groups={groups}
      active={active}
      onSelect={(id) => openSettings(id as SettingsSection)}
      onClose={closeSettings}
      // из шестерёнки на телефоне — список разделов; ссылкой на раздел («Как получить Плюс») — сразу он
      startWithList={section === 'account'}
      anchors
    >
      {active === 'account' ? (
        <AccountSection />
      ) : active === 'profile' ? (
        <ProfileSection />
      ) : active === 'premium' ? (
        <PremiumSection />
      ) : active === 'voice' ? (
        <VoiceSection />
      ) : active === 'notifications' ? (
        <NotificationsSection />
      ) : active === 'keybinds' ? (
        <KeybindsSection />
      ) : active === 'desktop' ? (
        <DesktopSection />
      ) : active === 'overlay' ? (
        <OverlaySection />
      ) : active === 'applications' ? (
        <ApplicationsSection />
      ) : active === 'releases' ? (
        <ReleasesSection />
      ) : active === 'help' ? (
        <HelpSection />
      ) : (
        <AppearanceSection />
      )}
    </SettingsFrame>
  )
}
